<template>
  <main class="profile-page">
    <section class="profile-shell">
      <RouterLink class="back-link" to="/crm/contacts">
        <v-icon size="16">mdi-arrow-left</v-icon>
        Back to Contacts
      </RouterLink>

      <div v-if="loading" class="empty-list">
        <v-progress-circular indeterminate size="24" color="primary" />
        <span>Loading profile…</span>
      </div>

      <div v-else-if="loadError" class="banner banner-error">{{ loadError }}</div>

      <template v-else-if="contact">
        <header class="profile-header">
          <div class="profile-title">
            <span class="detail-avatar">{{ (contact.contact_name || '?').charAt(0) }}</span>
            <div>
              <div class="detail-name-row">
                <h1>{{ contact.contact_name }}</h1>
                <span v-if="contact.is_repeat_customer" class="status-pill repeat-pill">
                  <v-icon size="14">mdi-star</v-icon>
                  Returning Customer
                </span>
                <span v-if="latestLead" class="status-pill" :class="`status-${latestLead.status}`">
                  <v-icon size="15">{{ statusIcons[latestLead.status] }}</v-icon>
                  Latest: {{ statusLabels[latestLead.status] }}
                </span>
              </div>
              <p>{{ contact.email || 'No email' }} · {{ contact.phone || 'No phone' }}</p>
            </div>
          </div>

          <button class="channel-button" type="button" @click="showEditForm = true">
            <v-icon size="17">mdi-pencil-outline</v-icon>
            <span>Edit</span>
          </button>
        </header>

        <div class="stats-row">
          <div class="stat-card">
            <strong>{{ contact.quotes.length }}</strong>
            <span>Quotes</span>
          </div>
          <div class="stat-card">
            <strong>${{ lifetimeValue.toFixed(2) }}</strong>
            <span>Lifetime value</span>
          </div>
          <div class="stat-card">
            <strong>{{ customerSince || '—' }}</strong>
            <span>Customer since</span>
          </div>
        </div>

        <div class="profile-grid">
          <div class="profile-main">
            <section class="info-card">
              <div class="section-header">
                <h3>Contact Info</h3>
              </div>
              <div class="detail-grid">
                <div class="field-group">
                  <span>Email</span>
                  <strong>{{ contact.email || '—' }}</strong>
                </div>
                <div class="field-group">
                  <span>Phone</span>
                  <strong>{{ contact.phone || '—' }}</strong>
                </div>
                <div class="field-group">
                  <span>City / Province</span>
                  <strong>{{ [contact.city, contact.province].filter(Boolean).join(', ') || '—' }}</strong>
                </div>
                <div class="field-group">
                  <span>Postal Code</span>
                  <strong>{{ contact.postal_code || '—' }}</strong>
                </div>
              </div>
            </section>

            <section class="conversation-panel">
              <div class="section-header">
                <h3>Activity</h3>
              </div>

              <div class="message-thread" ref="messageThreadEl">
                <template v-for="entry in contact.timeline" :key="`${entry.type}-${entry.id}`">
                  <div v-if="entry.type === 'lead_created'" class="timeline-status timeline-lead">
                    <v-icon size="15">mdi-account-plus-outline</v-icon>
                    <span>
                      New lead created<template v-if="entry.service"> — {{ entry.service }}</template>
                      <template v-if="entry.source"> ({{ sourceLabels[entry.source] || entry.source }})</template>
                    </span>
                    <span class="timeline-status-time">{{ formatFullTime(entry.created_at) }}</span>
                  </div>

                  <article
                    v-else-if="entry.type === 'message'"
                    class="message-bubble"
                    :class="{ contractor: messageIsOutbound(entry) }"
                  >
                    <div class="message-meta">
                      <span class="message-author-group">
                        <strong>{{ messageAuthor(entry) }}</strong>
                        <span class="channel-badge" :class="`channel-${entry.channel || 'email'}`">
                          <v-icon size="12">{{ channelIcons[entry.channel] || channelIcons.email }}</v-icon>
                          {{ channelLabels[entry.channel] || channelLabels.email }}
                        </span>
                      </span>
                      <span>{{ formatFullTime(entry.created_at) }}</span>
                    </div>
                    <p v-if="entry.subject" class="message-subject">{{ entry.subject }}</p>
                    <p class="message-body">{{ entry.body }}</p>
                  </article>

                  <div v-else-if="entry.type === 'note'" class="note-item timeline-note">
                    <div class="timeline-note-header">
                      <span class="timeline-note-label">
                        <v-icon size="12">mdi-note-text-outline</v-icon>
                        Internal note
                      </span>
                      <span class="note-source-badge" :class="{ general: !entry.lead_id }">
                        {{ entry.lead_id ? leadLabel(entry.lead_id) : 'General' }}
                      </span>
                    </div>
                    <p class="message-body">{{ entry.body }}</p>
                    <span class="timeline-status-time">{{ formatFullTime(entry.created_at) }}</span>
                  </div>

                  <div v-else-if="entry.type === 'status_change'" class="timeline-status">
                    <v-icon size="15">{{ statusIcons[entry.to_status] }}</v-icon>
                    <span>
                      Marked as <strong>{{ statusLabels[entry.to_status] || entry.to_status }}</strong>
                      <template v-if="entry.lead_service"> ({{ entry.lead_service }})</template>
                    </span>
                    <span class="timeline-status-time">{{ formatFullTime(entry.created_at) }}</span>
                  </div>

                  <div v-else-if="entry.type === 'quote'" class="timeline-status timeline-quote">
                    <v-icon size="15">mdi-file-document-outline</v-icon>
                    <span>Quote <strong>{{ entry.quote_number }}</strong> — {{ entry.status }}</span>
                    <span class="timeline-status-time">{{ formatFullTime(entry.created_at) }}</span>
                  </div>

                  <div v-else-if="entry.type === 'job'" class="timeline-status timeline-job">
                    <v-icon size="15">mdi-briefcase-outline</v-icon>
                    <span>Job <strong>{{ entry.title }}</strong> — scheduled for {{ formatFullTime(entry.scheduled_start) }}</span>
                    <span class="timeline-status-time">{{ formatFullTime(entry.created_at) }}</span>
                  </div>
                </template>

                <p v-if="!contact.timeline.length" class="muted-note">No activity yet</p>
              </div>

              <div class="composer-row">
                <p v-if="noteError" class="banner banner-error">{{ noteError }}</p>
                <div class="note-box">
                  <textarea
                    v-model="noteDraft"
                    rows="3"
                    placeholder="Add an internal note… (Ctrl+Enter to add)"
                    @keydown.ctrl.enter.prevent="addNote"
                  ></textarea>
                  <button
                    class="note-button"
                    type="button"
                    :disabled="!noteDraft.trim() || noteSending"
                    title="Add note"
                    @click="addNote"
                  >
                    <v-icon size="19">mdi-plus</v-icon>
                  </button>
                </div>
              </div>
            </section>
          </div>

          <aside class="side-panel">
            <section class="quotes-panel">
              <div class="section-header">
                <h3>Quotes</h3>
                <RouterLink class="new-quote-link" :to="newQuoteRoute">+ New quote</RouterLink>
              </div>

              <div class="quotes-list">
                <div v-for="quote in contact.quotes" :key="quote.id" class="quote-item">
                  <span class="quote-number">{{ quote.quote_number }}</span>
                  <span class="status-pill" :class="`status-${quote.status === 'accepted' ? 'won' : 'new'}`">
                    {{ quote.status }}
                  </span>
                  <span class="quote-total">${{ Number(quote.total_amount || 0).toFixed(2) }}</span>
                  <RouterLink
                    v-if="jobForQuote(quote)"
                    class="schedule-job-link scheduled"
                    to="/jobs"
                    title="View job"
                  >
                    <v-icon size="13">mdi-check</v-icon>
                    Scheduled
                  </RouterLink>
                  <button
                    v-else-if="quote.status === 'accepted'"
                    class="schedule-job-link"
                    type="button"
                    @click="openScheduleJob(quote)"
                  >
                    <v-icon size="13">mdi-calendar-plus</v-icon>
                    Schedule job
                  </button>
                </div>
                <p v-if="!contact.quotes.length" class="muted-note">No quotes yet</p>
              </div>
            </section>

            <section class="leads-panel">
              <div class="section-header">
                <h3>Leads</h3>
              </div>

              <div class="leads-list">
                <RouterLink v-for="lead in contact.leads" :key="lead.id" class="lead-item" to="/crm/leads">
                  <div class="lead-item-top">
                    <span class="status-pill" :class="`status-${lead.status}`">
                      {{ statusLabels[lead.status] }}
                    </span>
                    <span class="lead-item-date">{{ formatDate(lead.created_at) }}</span>
                  </div>
                  <span class="lead-item-service">{{ lead.service || 'General inquiry' }}</span>
                  <span class="lead-item-source">{{ sourceLabels[lead.source] || lead.source || '—' }}</span>
                </RouterLink>
                <p v-if="!contact.leads.length" class="muted-note">No leads yet</p>
              </div>
            </section>

            <section class="reminders-panel">
              <div class="section-header">
                <h3>Reminders</h3>
              </div>

              <p v-if="reminderError" class="banner banner-error">{{ reminderError }}</p>

              <div class="reminders-list">
                <div
                  v-for="reminder in contact.reminders"
                  :key="reminder.id"
                  class="reminder-item"
                  :class="{ overdue: isOverdue(reminder), done: !!reminder.completed_at }"
                >
                  <input type="checkbox" :checked="!!reminder.completed_at" @change="toggleReminder(reminder)" />
                  <div class="reminder-body">
                    <strong>{{ formatFullTime(reminder.due_at) }}</strong>
                    <span v-if="reminder.note">{{ reminder.note }}</span>
                  </div>
                  <button type="button" class="reminder-delete" title="Delete reminder" @click="deleteReminder(reminder)">
                    <v-icon size="16">mdi-close</v-icon>
                  </button>
                </div>
                <p v-if="!contact.reminders.length" class="muted-note">No reminders set</p>
              </div>

              <div class="reminder-box">
                <input v-model="reminderDraft.due_at" type="datetime-local" />
                <input v-model="reminderDraft.note" type="text" placeholder="What to follow up on" />
                <button
                  class="note-button"
                  type="button"
                  :disabled="!reminderDraft.due_at || reminderSending"
                  title="Add reminder"
                  @click="addReminder"
                >
                  <v-icon size="19">mdi-plus</v-icon>
                </button>
              </div>
            </section>
          </aside>
        </div>
      </template>
    </section>

    <ContactFormModal
      v-model="showEditForm"
      mode="edit"
      :contact="contact"
      :company-id="contact?.company_id || ''"
      @saved="onContactSaved"
    />

    <JobFormModal
      v-model="showJobForm"
      mode="create"
      :company-id="contact?.company_id || ''"
      :prefill="jobPrefill"
      @saved="onJobScheduled"
    />
  </main>
</template>

<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { company, getAuthToken, API_BASE_URL } from '@/stores/appConfig'
import { logAction } from '@/utils/logAction'
import ContactFormModal from '@/components/leads/ContactFormModal.vue'
import JobFormModal from '@/components/jobs/JobFormModal.vue'
import { statusLabels, statusIcons, sourceLabels } from '@/constants/leads'

const channelLabels = { email: 'Email', sms: 'Text' }
const channelIcons = { email: 'mdi-email-outline', sms: 'mdi-message-text-outline' }

const route = useRoute()

const contact = ref(null)
const loading = ref(false)
const loadError = ref('')

const noteDraft = ref('')
const noteSending = ref(false)
const noteError = ref('')

const reminderDraft = ref({ due_at: '', note: '' })
const reminderSending = ref(false)
const reminderError = ref('')

const showEditForm = ref(false)

const showJobForm = ref(false)
const jobPrefill = ref(null)

const messageThreadEl = ref(null)

async function authHeaders() {
  const token = await getAuthToken()
  return token ? { Authorization: `Bearer ${token}` } : {}
}

async function scrollThreadToBottom() {
  await nextTick()
  const el = messageThreadEl.value
  if (el) el.scrollTop = el.scrollHeight
}

async function fetchProfile() {
  loading.value = true
  loadError.value = ''

  try {
    const headers = await authHeaders()
    const response = await fetch(`${API_BASE_URL}/contacts/${route.params.id}`, { headers })
    const data = await response.json().catch(() => ({}))

    if (!response.ok || !data.ok) {
      throw new Error(data.error || 'Failed to load customer profile.')
    }

    contact.value = {
      ...data.contact,
      is_repeat_customer: !!data.is_repeat_customer,
      leads: data.leads || [],
      internalNotes: data.notes || [],
      quotes: data.quotes || [],
      jobs: data.jobs || [],
      reminders: data.reminders || [],
      statusHistory: data.statusHistory || [],
      timeline: data.timeline || [],
    }
    scrollThreadToBottom()
  } catch (err) {
    loadError.value = err.message || 'Failed to load customer profile.'
  } finally {
    loading.value = false
  }
}

onMounted(fetchProfile)
watch(() => route.params.id, fetchProfile)

const latestLead = computed(() => contact.value?.leads?.[0] || null)

function leadLabel(leadId) {
  const lead = (contact.value?.leads || []).find((item) => item.id === leadId)
  if (!lead) return `Lead #${leadId}`

  return lead.service || sourceLabels[lead.source] || lead.source || `Lead #${leadId}`
}

function formatFullTime(dateString) {
  if (!dateString) return ''

  return new Date(dateString).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function formatDate(dateString) {
  if (!dateString) return ''

  return new Date(dateString).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function messageIsOutbound(message) {
  return message.direction === 'outbound'
}

function messageAuthor(message) {
  if (messageIsOutbound(message)) {
    return company.company_name || 'You'
  }

  return contact.value?.contact_name || 'Customer'
}

function isOverdue(reminder) {
  return !reminder.completed_at && new Date(reminder.due_at).getTime() < Date.now()
}

const lifetimeValue = computed(() => {
  if (!contact.value) return 0

  return contact.value.quotes
    .filter((quote) => ['accepted', 'invoiced', 'paid'].includes(quote.status))
    .reduce((sum, quote) => sum + Number(quote.total_amount || 0), 0)
})

const customerSince = computed(() => {
  if (!contact.value) return ''

  const wonEntry = [...contact.value.statusHistory]
    .filter((entry) => entry.to_status === 'won')
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))[0]

  return wonEntry ? formatDate(wonEntry.created_at) : ''
})

const newQuoteRoute = computed(() => {
  if (!contact.value) return { path: '/quote' }

  return {
    path: '/quote',
    query: {
      contact_id: contact.value.id,
      company_id: contact.value.company_id,
      customer_name: contact.value.contact_name || '',
      customer_email: contact.value.email || '',
      customer_phone: contact.value.phone || '',
    },
  }
})

function onContactSaved(updated) {
  contact.value = { ...contact.value, ...updated }
}

function jobForQuote(quote) {
  return (contact.value?.jobs || []).find((job) => job.quote_id === quote.id)
}

function openScheduleJob(quote) {
  if (!contact.value) return

  jobPrefill.value = {
    quote_id: quote.id,
    contact_id: contact.value.id,
    lead_id: quote.lead_id,
    title: quote.title || `Job for ${contact.value.contact_name}`,
    address: quote.job_address || contact.value.address || '',
  }
  showJobForm.value = true
}

function onJobScheduled() {
  jobPrefill.value = null
  fetchProfile()
}

async function addNote() {
  const note = noteDraft.value.trim()

  if (!contact.value || !note || noteSending.value) return

  noteSending.value = true
  noteError.value = ''

  try {
    const headers = await authHeaders()
    const response = await fetch(`${API_BASE_URL}/contacts/${contact.value.id}/notes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...headers },
      body: JSON.stringify({ body: note }),
    })
    const data = await response.json().catch(() => ({}))

    if (!response.ok || !data.ok) {
      throw new Error(data.error || 'Failed to add note.')
    }

    contact.value.internalNotes.unshift(data.note)
    contact.value.timeline = [...contact.value.timeline, { ...data.note, type: 'note' }].sort(
      (a, b) => new Date(a.created_at) - new Date(b.created_at)
    )
    noteDraft.value = ''
    scrollThreadToBottom()

    const token = await getAuthToken()
    logAction(token, {
      action: 'lead.note-add',
      label: 'Added a note to contact',
      company_id: contact.value.company_id,
      metadata: { contact_id: contact.value.id },
    })
  } catch (err) {
    noteError.value = err.message || 'Failed to add note.'
  } finally {
    noteSending.value = false
  }
}

async function addReminder() {
  if (!contact.value || !reminderDraft.value.due_at || reminderSending.value) return

  reminderSending.value = true
  reminderError.value = ''

  try {
    const headers = await authHeaders()
    const response = await fetch(`${API_BASE_URL}/contacts/${contact.value.id}/reminders`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...headers },
      body: JSON.stringify({
        due_at: reminderDraft.value.due_at,
        note: reminderDraft.value.note.trim() || undefined,
      }),
    })
    const data = await response.json().catch(() => ({}))

    if (!response.ok || !data.ok) {
      throw new Error(data.error || 'Failed to add reminder.')
    }

    contact.value.reminders.push(data.reminder)
    contact.value.reminders.sort((a, b) => new Date(a.due_at) - new Date(b.due_at))
    reminderDraft.value = { due_at: '', note: '' }

    const token = await getAuthToken()
    logAction(token, {
      action: 'lead.reminder-add',
      label: 'Set a reminder',
      company_id: contact.value.company_id,
      metadata: { contact_id: contact.value.id },
    })
  } catch (err) {
    reminderError.value = err.message || 'Failed to add reminder.'
  } finally {
    reminderSending.value = false
  }
}

async function toggleReminder(reminder) {
  if (!contact.value) return

  const completing = !reminder.completed_at

  try {
    const headers = await authHeaders()
    const response = await fetch(`${API_BASE_URL}/contacts/${contact.value.id}/reminders/${reminder.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...headers },
      body: JSON.stringify({ completed: completing }),
    })
    const data = await response.json().catch(() => ({}))

    if (!response.ok || !data.ok) {
      throw new Error(data.error || 'Failed to update reminder.')
    }

    const target = contact.value.reminders.find((item) => item.id === reminder.id)
    if (target) target.completed_at = data.reminder.completed_at

    if (completing) {
      const token = await getAuthToken()
      logAction(token, {
        action: 'lead.reminder-complete',
        label: 'Completed a reminder',
        company_id: contact.value.company_id,
        metadata: { contact_id: contact.value.id, reminder_id: reminder.id },
      })
    }
  } catch (err) {
    reminderError.value = err.message || 'Failed to update reminder.'
  }
}

async function deleteReminder(reminder) {
  if (!contact.value) return

  try {
    const headers = await authHeaders()
    const response = await fetch(`${API_BASE_URL}/contacts/${contact.value.id}/reminders/${reminder.id}`, {
      method: 'DELETE',
      headers,
    })
    const data = await response.json().catch(() => ({}))

    if (!response.ok || !data.ok) {
      throw new Error(data.error || 'Failed to delete reminder.')
    }

    contact.value.reminders = contact.value.reminders.filter((item) => item.id !== reminder.id)
  } catch (err) {
    reminderError.value = err.message || 'Failed to delete reminder.'
  }
}
</script>

<style scoped>
.profile-page {
  min-height: calc(100dvh - 70px);
  padding: 28px 18px 104px;
  background: var(--surface-alt);
  color: var(--text-primary);
}

.profile-shell {
  width: min(1200px, 100%);
  margin: 0 auto;
}

.back-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 16px;
  color: var(--text-secondary);
  font-size: 0.86rem;
  font-weight: 700;
  text-decoration: none;
}

.back-link:hover {
  color: #1f5c4d;
}

.banner {
  margin-bottom: 14px;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 0.86rem;
}

.banner-error {
  color: #8a2b1f;
  background: #fdecea;
  border: 1px solid #f3c6bf;
}

.empty-list {
  min-height: 220px;
  display: grid;
  place-items: center;
  align-content: center;
  gap: 8px;
  color: var(--text-secondary);
  font-size: 0.9rem;
}

.profile-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 14px;
  margin-bottom: 16px;
}

.profile-title {
  min-width: 0;
  display: flex;
  gap: 12px;
}

.detail-avatar {
  width: 48px;
  height: 48px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  border-radius: 10px;
  background: var(--surface-alt);
  color: var(--text-primary);
  font-size: 1.1rem;
  font-weight: 850;
}

.detail-name-row {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.detail-name-row h1 {
  margin: 0;
  font-size: 1.6rem;
  line-height: 1.15;
  font-weight: 850;
}

.profile-title p {
  margin: 5px 0 0;
  color: var(--text-secondary);
  font-size: 0.9rem;
}

.channel-button {
  height: 38px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: 0 0 auto;
  padding: 0 13px;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  color: var(--text-secondary);
  background: var(--surface);
  font: inherit;
  font-size: 0.84rem;
  font-weight: 750;
  cursor: pointer;
}

.channel-button:hover {
  color: #1f5c4d;
  border-color: #9fc8ba;
  background: #f3faf7;
}

.status-pill {
  min-height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 750;
  white-space: nowrap;
}

.status-new {
  color: #8a4b0f;
  background: #fff1dc;
}

.status-contacted {
  color: #6a3fa0;
  background: #f2ebfb;
}

.status-quoted {
  color: #29528a;
  background: #e8f1ff;
}

.status-won {
  color: #1f5c4d;
  background: #e6f4ef;
}

.status-lost {
  color: var(--text-secondary);
  background: var(--surface-alt);
}

.repeat-pill {
  color: #b45309;
  background: #fff4e0;
}

.stats-row {
  display: grid;
  grid-template-columns: repeat(3, minmax(140px, 1fr));
  gap: 10px;
  margin-bottom: 16px;
}

.stat-card {
  padding: 14px 16px;
  background: var(--surface);
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  box-shadow: 0 10px 24px rgba(23, 35, 31, 0.05);
}

.stat-card strong {
  display: block;
  font-size: 1.3rem;
  line-height: 1.2;
}

.stat-card span {
  color: var(--text-secondary);
  font-size: 0.78rem;
}

.profile-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(280px, 360px);
  gap: 14px;
  align-items: start;
}

.profile-main {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.info-card,
.leads-panel,
.conversation-panel,
.side-panel > * {
  background: var(--surface);
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  box-shadow: 0 14px 34px rgba(23, 35, 31, 0.06);
}

.conversation-panel {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.section-header {
  min-height: 50px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 13px 14px;
  border-bottom: 1px solid var(--border-subtle);
}

.section-header h3 {
  margin: 0;
  color: var(--text-primary);
  font-size: 0.94rem;
  font-weight: 850;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.field-group {
  min-width: 0;
  padding: 14px 16px;
  border-right: 1px solid var(--border-subtle);
  border-top: 1px solid var(--border-subtle);
}

.field-group span {
  display: block;
  margin-bottom: 5px;
  color: var(--text-secondary);
  font-size: 0.72rem;
  font-weight: 750;
  text-transform: uppercase;
}

.field-group strong {
  display: block;
  min-width: 0;
  overflow: hidden;
  color: var(--text-primary);
  font-size: 0.84rem;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.leads-list {
  display: grid;
  gap: 8px;
  padding: 14px;
}

.lead-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px 11px;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  background: var(--surface-alt);
  font-size: 0.86rem;
  color: var(--text-primary);
  text-decoration: none;
}

/* #f3faf7 is a fixed light tint, not theme-reactive — so the service/source/
   date text inside can't stay on var(--text-primary)/var(--text-secondary)
   either, or it goes near-white on a light background in dark mode. */
.lead-item:hover {
  border-color: #9fc8ba;
  background: #f3faf7;
}

.lead-item:hover .lead-item-service {
  color: #17231f;
}

.lead-item:hover .lead-item-source,
.lead-item:hover .lead-item-date {
  color: #56635e;
}

.lead-item-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.lead-item-service {
  overflow: hidden;
  color: var(--text-primary);
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lead-item-source,
.lead-item-date {
  color: var(--text-secondary);
  font-size: 0.8rem;
  white-space: nowrap;
}

.message-thread {
  min-height: 220px;
  max-height: 420px;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px;
  overflow-y: auto;
}

.message-bubble {
  width: min(620px, 92%);
  padding: 12px 14px;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  background: var(--surface);
}

.message-bubble.contractor {
  align-self: flex-end;
  color: #0f332c;
  border-color: #b9dbcf;
  background: #ecf8f4;
}

.message-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 6px;
  color: var(--text-secondary);
  font-size: 0.74rem;
}

.message-meta strong {
  color: var(--text-primary);
}

.message-author-group {
  min-width: 0;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.channel-badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  flex: 0 0 auto;
  padding: 2px 7px;
  border-radius: 999px;
  font-size: 0.68rem;
  font-weight: 750;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.channel-email {
  color: #29528a;
  background: #e8f1ff;
}

.channel-sms {
  color: #6a3fa0;
  background: #f2ebfb;
}

.message-subject {
  margin: 0 0 4px;
  color: var(--text-secondary);
  font-size: 0.78rem;
  font-style: italic;
}

.message-bubble .message-body {
  margin: 0;
  color: var(--text-primary);
  font-size: 0.92rem;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
}

.timeline-note {
  width: min(620px, 92%);
  padding: 12px 14px;
  border: 1px solid #f0e2b8;
  border-radius: 8px;
  background: #fffaef;
}

.timeline-note-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 4px;
}

.timeline-note-label {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #8a6d1f;
  font-size: 0.72rem;
  font-weight: 750;
  text-transform: uppercase;
}

.note-source-badge {
  overflow: hidden;
  flex: 0 0 auto;
  max-width: 55%;
  padding: 2px 8px;
  border-radius: 999px;
  color: #1f5c4d;
  background: #e6f4ef;
  font-size: 0.68rem;
  font-weight: 750;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.note-source-badge.general {
  color: var(--text-secondary);
  background: var(--surface-alt);
}

.timeline-note .message-body {
  margin: 0 0 6px;
  color: #4a3f1f;
  font-size: 0.9rem;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
}

.timeline-status {
  display: flex;
  align-items: center;
  gap: 8px;
  align-self: center;
  padding: 6px 12px;
  color: var(--text-secondary);
  font-size: 0.82rem;
}

.timeline-status .v-icon {
  color: var(--text-secondary);
}

.timeline-status-time {
  color: var(--text-secondary);
  font-size: 0.74rem;
}

.muted-note {
  margin: 0;
  color: var(--text-secondary);
  font-size: 0.88rem;
  padding: 14px;
}

.leads-list .muted-note {
  padding: 0 0 8px;
}

.composer-row {
  display: grid;
  gap: 10px;
  padding: 14px;
  border-top: 1px solid var(--border-subtle);
  background: var(--surface);
}

.note-box {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 9px;
}

textarea {
  width: 100%;
  min-width: 0;
  resize: vertical;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  padding: 11px 12px;
  color: var(--text-primary);
  background: var(--surface-alt);
  font: inherit;
  font-size: 0.9rem;
  line-height: 1.45;
  outline: none;
}

.note-button {
  width: 38px;
  height: 38px;
  align-self: end;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  color: var(--text-secondary);
  background: var(--surface);
  cursor: pointer;
}

.note-button:hover {
  color: #1f5c4d;
  border-color: #9fc8ba;
  background: #f3faf7;
}

.note-button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.side-panel {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.new-quote-link {
  color: #1f7a63;
  font-size: 0.8rem;
  font-weight: 750;
  text-decoration: none;
}

.new-quote-link:hover {
  text-decoration: underline;
}

.quotes-list {
  display: grid;
  gap: 8px;
  padding: 14px;
}

.quote-item {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  align-items: center;
  gap: 8px;
  padding: 10px 11px;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  background: var(--surface-alt);
  font-size: 0.86rem;
}

.quote-number {
  overflow: hidden;
  color: var(--text-primary);
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.quote-total {
  color: var(--text-primary);
  font-weight: 700;
}

.schedule-job-link {
  grid-column: 1 / -1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 6px 10px;
  border: 1px solid #9fc8ba;
  border-radius: 8px;
  color: #1f5c4d;
  background: #f3faf7;
  font: inherit;
  font-size: 0.8rem;
  font-weight: 750;
  text-decoration: none;
  cursor: pointer;
}

.schedule-job-link:hover {
  background: #e6f4ef;
}

.schedule-job-link.scheduled {
  border-color: var(--border-subtle);
  color: var(--text-secondary);
  background: var(--surface-alt);
}

.reminders-list {
  display: grid;
  gap: 8px;
  padding: 14px;
}

.reminder-item {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 9px;
  padding: 10px 11px;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  background: var(--surface-alt);
}

.reminder-item.overdue {
  border-color: #f3c6bf;
  background: #fdecea;
}

.reminder-item.done {
  opacity: 0.55;
}

.reminder-body {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.reminder-body strong {
  font-size: 0.84rem;
}

.reminder-body span {
  overflow: hidden;
  color: var(--text-secondary);
  font-size: 0.8rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.reminder-delete {
  width: 26px;
  height: 26px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 6px;
  color: var(--text-secondary);
  background: transparent;
  cursor: pointer;
}

.reminder-delete:hover {
  color: #8a2b1f;
  background: #fdecea;
}

.reminder-box {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) auto;
  gap: 9px;
  padding: 0 14px 14px;
}

.reminder-box input[type='text'],
.reminder-box input[type='datetime-local'] {
  min-width: 0;
  height: 38px;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  padding: 0 10px;
  color: var(--text-primary);
  background: var(--surface-alt);
  font: inherit;
  font-size: 0.84rem;
  outline: none;
}

@media (max-width: 900px) {
  .profile-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .detail-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .profile-header {
    flex-direction: column;
  }

  .lead-item-source {
    display: none;
  }
}
</style>
